/**
 * Holding Detail Hook
 *
 * Fetches a single holding with optional related data using the Hono RPC client
 * with TanStack Query for caching and state management.
 */

import { useQuery, useQueryClient, queryOptions } from "@tanstack/react-query";
import { api, unwrapResponse, ApiError } from "@/lib/api";
import type {
  Holding,
  Fund,
  RankHistoryEntry,
  Transaction,
  TaxLot,
  TaxImpact,
  HoldingId,
} from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Optional includes for holding detail requests
 * Controls what related data is fetched alongside the holding
 */
export interface HoldingIncludes {
  /** Include transaction history (purchases, redemptions, etc.) */
  transactions?: boolean;
  /** Include FIFO tax lot breakdown */
  taxLots?: boolean;
  /** Include estimated tax impact for full redemption */
  taxImpact?: boolean;
}

/**
 * Fund data with rank history included
 */
export interface FundWithRankHistory extends Fund {
  rankHistory: RankHistoryEntry[];
}

/**
 * Complete holding detail response
 * Optional fields are present based on the includes parameter
 */
export interface HoldingDetail {
  holding: Holding;
  fund: FundWithRankHistory;
  transactions?: Transaction[];
  taxLots?: TaxLot[];
  taxImpact?: TaxImpact;
}

/**
 * Parameters for the holding query
 */
export interface HoldingQueryParams {
  holdingId: HoldingId | string;
  includes?: HoldingIncludes;
}

// ============================================================================
// Query Key Factory
// ============================================================================

/**
 * Query key factory for holding-related queries
 * Provides consistent key structure for cache management
 */
export const holdingKeys = {
  /** Root key for all holding queries */
  all: ["holdings"] as const,
  /** Key for listing holdings */
  lists: () => [...holdingKeys.all, "list"] as const,
  /** Key for a single holding detail */
  detail: (id: string) => [...holdingKeys.all, "detail", id] as const,
  /** Key for a single holding with specific includes */
  detailWithIncludes: (id: string, includes?: HoldingIncludes) =>
    [...holdingKeys.all, "detail", id, includes ?? {}] as const,
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Converts includes object to comma-separated query string
 */
function buildIncludesParam(includes?: HoldingIncludes): string | undefined {
  if (!includes) return undefined;

  const parts: string[] = [];
  if (includes.transactions) parts.push("transactions");
  if (includes.taxLots) parts.push("taxLots");
  if (includes.taxImpact) parts.push("taxImpact");

  return parts.length > 0 ? parts.join(",") : undefined;
}

// ============================================================================
// Query Options Factory
// ============================================================================

/**
 * Creates query options for fetching a holding detail
 * Can be used with useQuery, prefetching, or useSuspenseQuery
 *
 * @param params - The query parameters including holdingId and optional includes
 * @returns TanStack Query options object
 *
 * @example
 * ```tsx
 * // Use with useQuery
 * const { data } = useQuery(holdingQueryOptions({ holdingId: 'hld_001' }));
 *
 * // Use with prefetching
 * queryClient.prefetchQuery(holdingQueryOptions({
 *   holdingId: 'hld_001',
 *   includes: { transactions: true, taxImpact: true }
 * }));
 * ```
 */
export function holdingQueryOptions({ holdingId, includes }: HoldingQueryParams) {
  const includeParam = buildIncludesParam(includes);

  return queryOptions({
    queryKey: holdingKeys.detailWithIncludes(holdingId, includes),
    queryFn: async (): Promise<HoldingDetail> => {
      // Get the base URL from the Hono client
      const baseUrl = api.v1.holdings[":id"].$url({
        param: { id: holdingId },
      });

      // Append query params if includes are specified
      const url = new URL(baseUrl);
      if (includeParam) {
        url.searchParams.set("include", includeParam);
      }

      const response = await fetch(url.toString());
      return unwrapResponse<HoldingDetail>(response);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    // Enable retry for transient failures
    retry: (failureCount, error) => {
      // Don't retry on 404s
      if (error instanceof ApiError && error.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching a single holding with optional related data
 *
 * @param holdingId - The unique identifier for the holding
 * @param includes - Optional object specifying which related data to include
 * @returns TanStack Query result with holding data, loading state, and error handling
 *
 * @example
 * ```tsx
 * function HoldingDetailScreen({ holdingId }: { holdingId: string }) {
 *   const {
 *     data,
 *     isLoading,
 *     isError,
 *     error,
 *     refetch
 *   } = useHolding(holdingId, {
 *     transactions: true,
 *     taxImpact: true
 *   });
 *
 *   if (isLoading) return <HoldingSkeleton />;
 *   if (isError) return <ErrorDisplay error={error} onRetry={refetch} />;
 *
 *   return (
 *     <div>
 *       <HoldingHeader holding={data.holding} fund={data.fund} />
 *       {data.transactions && (
 *         <TransactionList transactions={data.transactions} />
 *       )}
 *       {data.taxImpact && (
 *         <TaxImpactCard taxImpact={data.taxImpact} />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useHolding(holdingId: HoldingId | string, includes?: HoldingIncludes) {
  return useQuery({
    ...holdingQueryOptions({ holdingId, includes }),
    // Only run query if holdingId is provided
    enabled: Boolean(holdingId),
  });
}

/**
 * Hook for fetching holding with all related data
 * Convenience wrapper that includes transactions, tax lots, and tax impact
 *
 * @param holdingId - The unique identifier for the holding
 * @returns TanStack Query result with complete holding data
 */
export function useHoldingComplete(holdingId: HoldingId | string) {
  return useHolding(holdingId, {
    transactions: true,
    taxLots: true,
    taxImpact: true,
  });
}

// ============================================================================
// Cache Invalidation Helpers
// ============================================================================

/**
 * Hook that provides cache invalidation utilities for holding data
 *
 * @returns Object with invalidation functions
 *
 * @example
 * ```tsx
 * function RefreshButton({ holdingId }: { holdingId: string }) {
 *   const { invalidateHolding, invalidateAllHoldings } = useHoldingInvalidation();
 *
 *   const handleRefresh = async () => {
 *     await invalidateHolding(holdingId);
 *   };
 *
 *   return <Button onClick={handleRefresh}>Refresh</Button>;
 * }
 * ```
 */
export function useHoldingInvalidation() {
  const queryClient = useQueryClient();

  return {
    /**
     * Invalidates a specific holding's cache
     * This will refetch the data on next access
     */
    invalidateHolding: (holdingId: string) =>
      queryClient.invalidateQueries({
        queryKey: holdingKeys.detail(holdingId),
      }),

    /**
     * Invalidates all holdings cache
     * Useful after bulk operations or sync
     */
    invalidateAllHoldings: () =>
      queryClient.invalidateQueries({
        queryKey: holdingKeys.all,
      }),

    /**
     * Refetches a specific holding immediately
     * Use when you need the data right away
     */
    refetchHolding: (holdingId: string, includes?: HoldingIncludes) =>
      queryClient.refetchQueries({
        queryKey: holdingKeys.detailWithIncludes(holdingId, includes),
      }),

    /**
     * Pre-fetches a holding into the cache
     * Useful for optimistic UI when navigating
     */
    prefetchHolding: (holdingId: string, includes?: HoldingIncludes) =>
      queryClient.prefetchQuery(holdingQueryOptions({ holdingId, includes })),

    /**
     * Updates the holding data in the cache optimistically
     * Useful for immediate UI updates before server confirmation
     */
    setHoldingData: (holdingId: string, data: HoldingDetail, includes?: HoldingIncludes) =>
      queryClient.setQueryData(
        holdingKeys.detailWithIncludes(holdingId, includes),
        data
      ),

    /**
     * Gets the current cached holding data
     * Returns undefined if not in cache
     */
    getHoldingData: (holdingId: string, includes?: HoldingIncludes) =>
      queryClient.getQueryData<HoldingDetail>(
        holdingKeys.detailWithIncludes(holdingId, includes)
      ),
  };
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/**
 * Type guard for ApiError
 */
export function isHoldingApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Checks if error is a 404 not found
 */
export function isHoldingNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * Get user-friendly error message for holding errors
 */
export function getHoldingErrorMessage(error: unknown): string {
  if (isHoldingApiError(error)) {
    if (error.status === 404) {
      return "Holding not found. It may have been deleted.";
    }
    if (error.status >= 500) {
      return "Server error. Please try again later.";
    }
    return error.message;
  }

  if (error instanceof Error) {
    if (error.message.includes("fetch")) {
      return "Network error. Please check your connection.";
    }
    return error.message;
  }

  return "An unexpected error occurred while loading the holding.";
}
